import React from 'react';
import { IndexRoute, Redirect, Route } from 'react-router';

import { NOTIFICATIONS_PATH } from '@/shared/NotificationCenter/constants';
import { OrganiserPages } from '@/types/organiser';

import getEventRoutes from './Events';

import {
  LoadableAboutUsPage,
  LoadableBookingFormPage,
  LoadableBrandPartnershipsPage,
  LoadableCommentActivityPage,
  LoadableCommunityPage,
  LoadableGroupPage,
  LoadableJoinGroupPage,
  LoadableJoinGroupMutationPage,
  LoadableLeaderboardPage,
  LoadableLikeActivityPage,
  LoadableOnboardingPage,
  LoadablePrivateEmailPrefsPage,
  LoadablePrivateMyEventPage,
  LoadablePrivateMyEventsPage,
  LoadablePrivateProfileSettingsPage,
  LoadableSaveEventPage,
  LoadableSaveExternalBookingPage,
  LoadableUnlikeActivityPage,
  LoadableUnsaveEventPage,
  LoadableUnsaveExternalBookingPage,
  LoadableUnsubscribeEmailPage,
  LoadableUserProfile,
  LoadableHomePageRouter,
  LoadableLayout,
  LoadableCategoryPage,
  LoadableAskQuestionPage,
  LoadableOrganiserClaimPage,
  LoadableOrganiserClaimByTokenPage,
  LoadableDynamicBookingForm,
  LoadableSavedEventsPage,
  LoadableNotificationsPage,
  LoadableOrganiserDashboardPage,
  LoadableLDTDashboardPage,
} from './helpers/loadables';

// These components loadable code is nested below their redux fetch layer.
import OrganiserDetailPage from '../../containers/OrganiserDetailPage';

import { redirectToUSfolder } from './helpers/helpers';

export default (authService) => (
  <>
    <Route component={LoadableLayout}>
      {/* Homepage route */}
      <IndexRoute
        component={LoadableHomePageRouter}
        whiteBackground
        showExtendedFooter
      />
      {/* Country-specific homepages */}
      <Route
        path="/us"
        component={LoadableHomePageRouter}
        locale="US"
        whiteBackground
        showExtendedFooter
      />
      <Route
        path="/au"
        component={LoadableHomePageRouter}
        locale="AU"
        whiteBackground
        showExtendedFooter
      />
      <Route
        path="/gb"
        component={LoadableHomePageRouter}
        locale="GB"
        whiteBackground
        showExtendedFooter
      />
      <Route path="/about" component={LoadableAboutUsPage} whiteBackground />

      {/* Community */}
      <Route
        path="/community"
        whiteBackground
        component={LoadableCommunityPage}
      />

      {/* Notifications */}
      <Route
        onEnter={authService.authenticateRoute}
        path={NOTIFICATIONS_PATH}
        whiteBackground
        component={LoadableNotificationsPage}
      />

      {/* Saved Events */}
      <Route
        onEnter={authService.authenticateRoute}
        path="/saved-events"
        whiteBackground
        component={LoadableSavedEventsPage}
      />

      {/* My Bookings */}
      <Route
        onEnter={authService.authenticateRoute}
        path="/my-events"
        component={LoadablePrivateMyEventsPage}
        whiteBackground
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/my-events/:bookingId/:eventId"
        component={LoadablePrivateMyEventPage}
        whiteBackground
      />

      <Route
        path="/onboarding"
        noFooter
        noBanner
        whiteBackground
        onEnter={authService.authenticateRoute}
        component={LoadableOnboardingPage}
      />
      {/* Discovery pages
            EXAMPLES
            /:localeSlug/:sportSlug-events                              >   /gb/running-events
            /:localeSlug/:sportSlug-events/:tagSlug                     >   /au/triathlon-events/olympic
            /:localeSlug/:sportSlug-events/in-:locationSlug             >   /gb/running-events/in-london
            /:localeSlug/:sportSlug-events/:tagSlug/in-:locationSlug    >   /us/running-events/half-marathon/in-california
      */}
      <Route
        path="/:localeSlug/:sportSlug-events(/in-:locationSlug)"
        component={LoadableCategoryPage}
        whiteBackground
        canUsePartnerTheme
        useDiscoveryHeader
        noFooter
      />
      <Route
        path="/:localeSlug/:sportSlug-events(/:tagSlug)(/in-:locationSlug)"
        component={LoadableCategoryPage}
        whiteBackground
        canUsePartnerTheme
        useDiscoveryHeader
        noFooter
      />
      <Route
        path="/:sportSlug-events(/in-:locationSlug)"
        component={LoadableCategoryPage}
        onEnter={redirectToUSfolder}
        whiteBackground
        canUsePartnerTheme
        useDiscoveryHeader
        noFooter
      />
      <Route
        path="/:sportSlug-events(/:tagSlug)(/in-:locationSlug)"
        component={LoadableCategoryPage}
        onEnter={redirectToUSfolder}
        whiteBackground
        canUsePartnerTheme
        useDiscoveryHeader
        noFooter
      />

      {/* Groups */}
      <Route
        path="/g/:groupId/:eventId/:encodedInvitedByUserId"
        component={LoadableJoinGroupPage}
        noFooter
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/g/:groupId/:encodedInvitedByUserId"
        component={LoadableJoinGroupPage}
        noFooter
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/g/:groupId"
        component={LoadableGroupPage}
        noFooter
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/g"
        component={LoadableGroupPage}
        noFooter
      />

      {/* Mutations */}
      <Route
        path="/ask-question/:variables"
        component={LoadableAskQuestionPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/comment-activity/:variables"
        component={LoadableCommentActivityPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/like-activity/:variables"
        component={LoadableLikeActivityPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/unlike-activity/:variables"
        component={LoadableUnlikeActivityPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/save-event/:variables"
        component={LoadableSaveEventPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/unsave-event/:variables"
        component={LoadableUnsaveEventPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/save-externalbooking/:variables"
        component={LoadableSaveExternalBookingPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/unsave-externalbooking/:variables"
        component={LoadableUnsaveExternalBookingPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/join-group/:variables"
        component={LoadableJoinGroupMutationPage}
        onEnter={authService.authenticateRoute}
      />
      <Route
        path="/unsubscribe-email/:variables"
        component={LoadableUnsubscribeEmailPage}
      />
      {getEventRoutes(authService)}
      {/* User pages */}
      <Route path="/invite" onEnter={authService.authenticateRoute}>
        <Redirect from="facebook" to="/profile" />
        <Redirect from="strava" to="/profile" />
      </Route>
      {/* <Route path="/home" component={UserHomepage} whiteBackground onEnter={authService.authenticateRoute} /> */}

      {/* Profile */}
      <Route
        onEnter={authService.authenticateRoute}
        path="/my-profile/settings"
        component={LoadablePrivateProfileSettingsPage}
        whiteBackground
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/my-profile/emailprefs"
        component={LoadablePrivateEmailPrefsPage}
        whiteBackground
      />
      <Route
        onEnter={authService.authenticateRoute}
        path="/my-profile"
        whiteBackground
        component={LoadableUserProfile}
      />
      <Route
        path="/profile/:profileId"
        component={LoadableUserProfile}
        whiteBackground
      />
      <Route path="/profile" component={LoadableUserProfile} whiteBackground />
      {/* Booking page */}
      <Route
        path="/book/:ticketId"
        component={LoadableBookingFormPage}
        noFooter
        whiteBackground
        canUsePartnerTheme
      />
      <Route
        path="/dbook/admin(/:ticketId)(/:jobId)"
        component={LoadableDynamicBookingForm}
        noFooter
        whiteBackground
        canUsePartnerTheme
        // TODO: @ree5yd make admin check work
        // for now seems to redirect to /logout no matter what
        // might be auth upgrade related
      />
      <Route
        path="/dbook/ticket/:ticketId(/:jobId)"
        component={LoadableDynamicBookingForm}
        noFooter
        whiteBackground
        canUsePartnerTheme
      />
      {/* Leaderboard page */}
      <Route
        path="/gb/top-rated(/:category)(/:location)"
        component={LoadableLeaderboardPage}
        whiteBackground
      />
      {/* Organiser claim */}
      <Route
        path="/organiser-claim"
        onEnter={authService.authenticateRoute}
        component={LoadableOrganiserClaimPage}
      />
      <Route
        path="/organiser-claim-by-token"
        onEnter={authService.authenticateRoute}
        component={LoadableOrganiserClaimByTokenPage}
      />
      {/* Organiser page */}
      <Route
        path="/o/(:organiserSlug-):organiserId"
        component={OrganiserDetailPage}
      />
      {/* Organiser panel */}
      <Route
        path="dashboard/ldt/latest"
        component={LoadableLDTDashboardPage}
        whiteBackground
        onEnter={authService.authenticateRoute}
        noFooter
        inLDTView
      />
      <Route
        path="dashboard/o/:organiserId/e/:eventId"
        component={LoadableOrganiserDashboardPage}
        inOrganiserView
        whiteBackground
        onEnter={authService.authenticateRoute}
        organiserPage={OrganiserPages.START_LIST}
      />
      <Route
        path="dashboard/o/:organiserId/settings"
        component={LoadableOrganiserDashboardPage}
        inOrganiserView
        whiteBackground
        onEnter={authService.authenticateRoute}
        organiserPage={OrganiserPages.ORGANISER_SETTINGS}
      />
      <Route
        path="dashboard/o/:organiserId/latest"
        component={LoadableOrganiserDashboardPage}
        inOrganiserView
        whiteBackground
        onEnter={authService.authenticateRoute}
        organiserPage={OrganiserPages.LATEST_ENTRIES}
      />
      <Route
        path="dashboard/o/:organiserId"
        component={LoadableOrganiserDashboardPage}
        inOrganiserView
        whiteBackground
        onEnter={authService.authenticateRoute}
        organiserPage={OrganiserPages.EVENT_LIST}
      />
      {/* Brand Partners page */}
      <Route
        path="/brand-partnerships"
        component={LoadableBrandPartnershipsPage}
        whiteBackground
      />
      {/* Tag Pages. If any route is not matched,
          check if a category exists for the given tag.
          EXAMPLES
          /trail-runs              > redirects to /us/trail-runs
          /us/trail-runs
          /gb/trail-runs/in-london > *** Not yet implemented ***
      */}
      <Route
        path="*"
        component={LoadableCategoryPage}
        onEnter={redirectToUSfolder}
        whiteBackground
        canUsePartnerTheme
        useDiscoveryHeader
      />
    </Route>
  </>
);
